// Money-flow Sankey (PLAN §9; task 35): asset / transaction flows between Actors, filterable by period and currency.
// The API reads the flow claims through the claims service (relation claims of a `flow` type such as "donated to",
// "funded by" read the other way round, and the rows of an actor's "Funding sources" table); everything here is pure
// and shared: the shapes, the amount a flow counts for, filters, and the Sankey graph (links summed per payer →
// recipient pair, cycles set aside because a Sankey can't draw them).

import type { EntitySummary, ReviewStatus } from './claims';
import { maxSensitivity } from './claims';
import type { AmountQualifier } from './schema/values';
import { isoEnd, isoStart, worseStatus } from './timeline';
import type { Sensitivity } from './uploads';

/** Focus actors on one Sankey (the URL `?ids=`). */
export const FLOWS_MAX_FOCUS = 12;
/** Flow claims read per request; more are reported as `truncated`. */
export const FLOWS_MAX_CLAIMS = 2000;
/** Relation types whose money runs from the object to the subject ("X funded by Y": Y pays X). */
export const FLOW_REVERSED_RELATIONS: readonly string[] = ['funded_by'];
/** Relation types that carry money besides the `flow` category (read subject → object unless reversed above). */
export const FLOW_EXTRA_RELATIONS: readonly string[] = ['funded_by'];
/** Table fields whose rows are flows into the profile's actor: the row's `source` column pays the subject. */
export const FLOW_ROW_FIELDS: readonly string[] = ['funding_sources'];
/** An undisclosed flow is drawn this share of the largest link, so it stays visible without claiming an amount. */
export const UNDISCLOSED_SHARE = 0.03;
/** The currency key for amounts written without a currency. */
export const NO_CURRENCY = 'none';

type Status = Exclude<ReviewStatus, 'rejected'>;

/** One flow claim (or one "Funding sources" row), payer → recipient. */
export interface FlowItem {
  /** Unique: the claim id, or `<claim id>:<source id>` for a table row. */
  id: string;
  claimId: string;
  /** The claim's subject: the profile it is recorded on. */
  subjectId: string;
  /** Payer and recipient (Actor ids). */
  from: string;
  to: string;
  /** What the payer did, from the payer's side: "donated to", "funds", "Funding sources". */
  label: string;
  /** The relation type key, or the table field key. */
  predicate: string;
  /** Bounds as stated; both null when undisclosed or absent. */
  amountMin: number | null;
  amountMax: number | null;
  /** ISO 4217, or null when not stated. */
  currency: string | null;
  /** null: no amount was written at all (counted as undisclosed). */
  qualifier: AmountQualifier | null;
  /** The amount as written ("€2–10M", "undisclosed"). */
  amountText: string | null;
  /** The period as ISO prefixes; both null = undated. */
  periodFrom: string | null;
  periodTo: string | null;
  openEnd: boolean;
  periodText: string | null;
  status: Status;
  sensitivity: Sensitivity;
}

export interface FlowsData {
  /** Every actor a flow touches (with rules-mode styles). */
  entities: EntitySummary[];
  flows: FlowItem[];
  /** The focus actors that still exist, in the order asked for; empty = all flows. */
  focus: string[];
  /** More flow claims matched than were read. */
  truncated: boolean;
}

const ID = /^[0-9A-Za-z]{1,40}$/;

/** Focus ids from a comma list (URL `?ids=`), deduplicated, malformed ones dropped, at most `FLOWS_MAX_FOCUS`. */
export function parseFlowIds(v: unknown): string[] {
  const raw = Array.isArray(v) ? v.join(',') : typeof v === 'string' ? v : '';
  return [
    ...new Set(
      raw
        .split(',')
        .map(s => s.trim())
        .filter(s => ID.test(s)),
    ),
  ].slice(0, FLOWS_MAX_FOCUS);
}

/**
 * The amount a flow counts for on the chart: the stated amount, or the middle of a range; null when undisclosed.
 * An open bound ("over €1M", "up to 500k") counts as the bound it states.
 */
export function flowValue(f: Pick<FlowItem, 'amountMin' | 'amountMax' | 'qualifier'>): number | null {
  if (!f.qualifier || f.qualifier === 'undisclosed') return null;
  const lo = f.amountMin ?? f.amountMax;
  const hi = f.amountMax ?? f.amountMin;
  if (lo === null || hi === null || !Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  const v = (lo + hi) / 2;
  return v > 0 ? v : null;
}

export const currencyKey = (f: Pick<FlowItem, 'currency'>) => f.currency ?? NO_CURRENCY;

export interface CurrencyOption {
  /** ISO code, or `NO_CURRENCY`. */
  key: string;
  /** Flows with an amount in this currency. */
  count: number;
  total: number;
}

/** Currencies with a disclosed amount, most flows first (then largest total, then code); "no currency" last. */
export function flowCurrencies(flows: readonly FlowItem[]): CurrencyOption[] {
  const m = new Map<string, CurrencyOption>();
  for (const f of flows) {
    const v = flowValue(f);
    if (v === null) continue;
    const k = currencyKey(f);
    const cur = m.get(k) ?? { key: k, count: 0, total: 0 };
    cur.count++;
    cur.total += v;
    m.set(k, cur);
  }
  const last = (c: CurrencyOption) => (c.key === NO_CURRENCY ? 1 : 0);
  return [...m.values()].sort((a, b) => last(a) - last(b) || b.count - a.count || b.total - a.total || a.key.localeCompare(b.key));
}

export interface FlowFilter {
  /** Currency key; flows in other currencies are left out. Undisclosed flows with no currency show in every one. */
  currency: string | null;
  fromYear?: number | null;
  toYear?: number | null;
  statuses?: Status[];
  /** Draw flows whose amount is undisclosed (default true). */
  undisclosed?: boolean;
}

/** A flow's period in ms; null when undated. Ongoing flows run to `now`; a single date covers its own span. */
export function flowSpan(
  f: Pick<FlowItem, 'periodFrom' | 'periodTo' | 'openEnd'>,
  now = Date.now(),
): { start: number; end: number } | null {
  if (!f.periodFrom && !f.periodTo) return null;
  const start = f.periodFrom ? isoStart(f.periodFrom) : Number.NEGATIVE_INFINITY;
  const end = f.periodTo ? isoEnd(f.periodTo) : f.openEnd ? now : f.periodFrom ? isoEnd(f.periodFrom) : Number.POSITIVE_INFINITY;
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return { start, end };
}

/** Whether a flow passes the filter. With a year filter, undated flows are left out (they can't be placed). */
export function flowMatches(f: FlowItem, filter: FlowFilter, now = Date.now()): boolean {
  if (filter.statuses && !filter.statuses.includes(f.status)) return false;
  const v = flowValue(f);
  if (v === null) {
    if (filter.undisclosed === false) return false;
    if (filter.currency && f.currency && f.currency !== filter.currency) return false;
  } else if (filter.currency && currencyKey(f) !== filter.currency) return false;
  if (filter.fromYear || filter.toYear) {
    const s = flowSpan(f, now);
    if (!s) return false;
    if (filter.fromYear && s.end < Date.UTC(filter.fromYear, 0, 1)) return false;
    if (filter.toYear && s.start > Date.UTC(filter.toYear + 1, 0, 1) - 1) return false;
  }
  return true;
}

export interface SankeyLinkData {
  /** `<from>→<to>`. */
  id: string;
  source: string;
  target: string;
  /** Width on the chart: the disclosed total, or a nominal width when every flow is undisclosed. */
  value: number;
  /** Sum of the disclosed amounts. */
  total: number;
  /** Some flows are ranges or estimates. */
  approximate: boolean;
  /** Flows in the link whose amount is undisclosed. */
  undisclosed: number;
  /** Every flow in the link is undisclosed: the width says nothing about the amount. */
  nominal: boolean;
  flows: FlowItem[];
  labels: string[];
  status: Status;
  sensitivity: Sensitivity;
}

export interface SankeyNodeData {
  id: string;
  /** Disclosed totals through the node (on drawn links). */
  paid: number;
  received: number;
}

export interface SankeyGraph {
  nodes: SankeyNodeData[];
  /** Links drawn on the chart (acyclic). */
  links: SankeyLinkData[];
  /** Links left off the chart because they close a loop (A pays B, B pays A); listed under it instead. */
  circular: SankeyLinkData[];
  /** Largest disclosed link total. */
  max: number;
}

/**
 * Sums flows per payer → recipient pair and lays out a graph a Sankey can draw. Loops are broken by keeping the
 * largest links: a link that would close a cycle goes to `circular`. Self-flows are dropped.
 */
export function buildSankey(flows: readonly FlowItem[]): SankeyGraph {
  const byPair = new Map<string, SankeyLinkData>();
  for (const f of flows) {
    if (f.from === f.to) continue;
    const id = `${f.from}→${f.to}`;
    const v = flowValue(f);
    const cur = byPair.get(id) ?? {
      id,
      source: f.from,
      target: f.to,
      value: 0,
      total: 0,
      approximate: false,
      undisclosed: 0,
      nominal: true,
      flows: [],
      labels: [],
      status: 'verified' as Status,
      sensitivity: 'standard' as Sensitivity,
    };
    cur.flows.push(f);
    if (!cur.labels.includes(f.label)) cur.labels.push(f.label);
    if (v === null) cur.undisclosed++;
    else {
      cur.total += v;
      cur.nominal = false;
      if (f.qualifier !== 'exact') cur.approximate = true;
    }
    cur.status = worseStatus(cur.status, f.status);
    cur.sensitivity = maxSensitivity(cur.sensitivity, f.sensitivity);
    byPair.set(id, cur);
  }
  const all = [...byPair.values()];
  const max = Math.max(0, ...all.map(l => l.total));
  const nominal = max > 0 ? max * UNDISCLOSED_SHARE : 1;
  for (const l of all) l.value = l.nominal ? nominal : l.total;
  // Largest first, so a loop drops its smallest link.
  all.sort((a, b) => b.value - a.value || a.id.localeCompare(b.id));

  const out = new Map<string, Set<string>>();
  const reaches = (from: string, to: string) => {
    const seen = new Set<string>([from]);
    const stack = [from];
    while (stack.length) {
      const n = stack.pop() as string;
      if (n === to) return true;
      for (const m of out.get(n) ?? []) {
        if (seen.has(m)) continue;
        seen.add(m);
        stack.push(m);
      }
    }
    return false;
  };
  const links: SankeyLinkData[] = [];
  const circular: SankeyLinkData[] = [];
  for (const l of all) {
    if (reaches(l.target, l.source)) {
      circular.push(l);
      continue;
    }
    links.push(l);
    const set = out.get(l.source) ?? new Set<string>();
    set.add(l.target);
    out.set(l.source, set);
  }

  const nodes = new Map<string, SankeyNodeData>();
  const node = (id: string) => {
    const n = nodes.get(id) ?? { id, paid: 0, received: 0 };
    nodes.set(id, n);
    return n;
  };
  for (const l of links) {
    node(l.source).paid += l.total;
    node(l.target).received += l.total;
  }
  for (const l of circular) {
    node(l.source);
    node(l.target);
  }
  return { nodes: [...nodes.values()], links, circular, max };
}

/** "EUR 1,250,000" · "1,250,000 (no currency)". */
export function formatAmount(v: number, currency: string | null): string {
  const n = new Intl.NumberFormat('en', { maximumFractionDigits: v < 100 ? 2 : 0 }).format(v);
  return currency && currency !== NO_CURRENCY ? `${currency} ${n}` : `${n} (no currency)`;
}

/** "EUR 50,000", "about EUR 6,000,000", "undisclosed" for a link. */
export function linkAmountLabel(l: Pick<SankeyLinkData, 'total' | 'approximate' | 'nominal' | 'undisclosed'>, currency: string | null) {
  if (l.nominal) return 'amount undisclosed';
  const base = `${l.approximate ? 'about ' : ''}${formatAmount(l.total, currency)}`;
  return l.undisclosed ? `${base} + ${l.undisclosed} undisclosed` : base;
}
